/**
 * RichTextEditor - Rich text editor using ReactQuill
 *
 * Features:
 * - Bold, italic, underline formatting
 * - Text alignment
 * - Ordered and bullet lists
 * - Text color
 * - Font sizes
 * - Fixed height with scrollable content
 *
 * @example
 * ```tsx
 * const editorRef = useRef<RichTextEditorRef>(null);
 *
 * <RichTextEditor
 *   ref={editorRef}
 *   initialValue="<p>Hello world</p>"
 *   placeholder="Start typing..."
 *   onChange={(content) => console.log(content)}
 * />
 *
 * // Get content
 * const html = editorRef.current?.getContent();
 *
 * // Set content
 * editorRef.current?.setContent("<p>New content</p>");
 * ```
 */

import React, { useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { cn } from '../../lib/utils';

export interface RichTextEditorProps {
  initialValue?: string;
  placeholder?: string;
  onChange?: (content: string) => void;
  className?: string;
  style?: React.CSSProperties;
}

export interface RichTextEditorRef {
  getContent: () => string;
  setContent: (content: string) => void;
  focus: () => void;
}

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(
  (
    { initialValue = '', placeholder = 'Start typing...', onChange, className = '', style },
    ref
  ) => {
    const quillRef = useRef<ReactQuill>(null);

    useImperativeHandle(ref, () => ({
      getContent: () => quillRef.current?.getEditor().root.innerHTML || '',
      setContent: (content: string) => {
        if (quillRef.current) {
          quillRef.current.getEditor().root.innerHTML = content;
        }
      },
      focus: () => quillRef.current?.focus(),
    }));

    const modules = useMemo(
      () => ({
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{ align: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ color: [] }],
          [{ size: ['small', false, 'large', 'huge'] }],
        ],
      }),
      []
    );

    const formats = ['bold', 'italic', 'underline', 'align', 'list', 'bullet', 'color', 'size'];

    const handleChange = (content: string) => {
      if (onChange) {
        onChange(content);
      }
    };

    return (
      <div className={cn('rich-text-editor-wrapper', className)} style={style}>
        <div className="quill-editor-container">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={initialValue}
            onChange={handleChange}
            placeholder={placeholder}
            modules={modules}
            formats={formats}
          />
        </div>
      </div>
    );
  }
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
